<script setup>
// NOTICE: KAPTION-INTEGRATION
// The two-factor authentication card in profile settings. When the
// KAPTION_WHATSAPP_2FA flag is on this replaces the TOTP card as the primary
// 2FA surface; TOTP stays reachable through the subdued link below. Renders
// its own FormSection so Index.vue only mounts it.
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { parseBoolean } from '@chatwoot/utils';
import FormSection from 'dashboard/components/FormSection.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useKaptionTwoStep } from 'dashboard/composables/useKaptionTwoStep';

const router = useRouter();
const route = useRoute();
const store = useStore();

const currentUser = computed(() => store.getters.getCurrentUser);
const isVerified = computed(
  () => currentUser.value?.kaption_whatsapp_2fa_verified === true
);
// two-step-verification.UPSTREAM.1: the upstream authenticator page is only
// open to people with the two-step feature on (the keys alone are not enough).
const { isEnabled: isKaptionTwoStepEnabled } = useKaptionTwoStep();
const isTotpAvailable = computed(
  () =>
    parseBoolean(window.chatwootConfig?.isMfaEnabled) &&
    isKaptionTwoStepEnabled.value
);

const navigate = () => {
  router.push({
    name: 'profile_settings_whatsapp_2fa',
    params: { accountId: route.params.accountId },
  });
};

const navigateToTotp = () => {
  router.push({
    name: 'profile_settings_mfa',
    params: { accountId: route.params.accountId },
  });
};
</script>

<template>
  <FormSection
    :title="$t('KAPTION_WA2FA.SECTION_TITLE')"
    :description="$t('KAPTION_WA2FA.SECTION_NOTE')"
  >
    <div class="bg-n-background rounded-xl p-4 border border-n-weak">
      <div class="flex flex-col xs:flex-row items-center justify-between gap-4">
        <div class="flex flex-col items-start gap-1.5">
          <div class="flex items-center gap-2">
            <Icon
              icon="i-lucide-shield-check"
              class="size-4 text-n-slate-10 flex-shrink-0"
            />
            <h5 class="text-sm font-semibold text-n-slate-12">
              {{ $t('KAPTION_WA2FA.TITLE') }}
            </h5>
            <span
              class="px-1.5 py-0.5 text-xs rounded-full"
              :class="
                isVerified
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-alpha-black2 text-n-slate-11'
              "
            >
              {{
                isVerified
                  ? $t('KAPTION_WA2FA.CARD_VERIFIED')
                  : $t('KAPTION_WA2FA.CARD_PENDING')
              }}
            </span>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ $t('KAPTION_WA2FA.CARD_DESCRIPTION') }}
          </p>
        </div>
        <Button
          type="button"
          faded
          :label="$t('KAPTION_WA2FA.CARD_BUTTON')"
          icon="i-lucide-settings"
          class="flex-shrink-0"
          @click="navigate"
        />
      </div>
      <div
        v-if="isTotpAvailable"
        class="flex items-center gap-1.5 mt-3 pt-3 border-t border-n-weak text-xs text-n-slate-10"
      >
        <Icon icon="i-lucide-smartphone" class="size-3.5 flex-shrink-0" />
        {{ $t('KAPTION_WA2FA.TOTP_ALTERNATIVE') }}
        <button
          class="text-n-slate-11 underline underline-offset-2 hover:text-n-slate-12"
          @click="navigateToTotp"
        >
          {{ $t('KAPTION_WA2FA.TOTP_LINK') }}
        </button>
      </div>
    </div>
  </FormSection>
</template>
